/**
 * @nanokart/tryon-studio-sdk
 * useMoments hook - Generate moment images
 */

import { useCallback } from 'react';
import { useStore } from './useStore';
import type { MomentResult } from '../../types/config';

export interface UseMomentsReturn {
  /** Results keyed by tryonCacheKey */
  results: Record<string, MomentResult[]>;
  /** Moment type currently being generated */
  isGenerating: string | null;
  /** Error message */
  error: string | null;
  /** Generate a moment */
  generateMoment: (tryonCacheKey: string, momentType: string) => Promise<MomentResult>;
  /** Get results for a try-on */
  getMomentResults: (tryonCacheKey: string) => MomentResult[];
  /** Check if a moment type has been generated */
  hasMoment: (tryonCacheKey: string, momentType: string) => boolean;
}

/** Available moment types — matches the Nanokart API values */
export const MOMENT_TYPES = {
  CREATIVE_WORKDAY: 'creative_workday',
  UNWIND: 'unwind',
  AFTER_HOURS: 'after_hours',
  ESCAPE: 'escape',
  GENTLE_CELEBRATION: 'gentle_celebration',
} as const;

export type MomentType = typeof MOMENT_TYPES[keyof typeof MOMENT_TYPES];

/**
 * Hook for generating moment images
 * 
 * @example
 * ```tsx
 * const { generateMoment, isGenerating, getMomentResults } = useMoments();
 * 
 * const handleGenerateMoment = async () => {
 *   await generateMoment(tryonResult.cacheKey, 'beach');
 * };
 * ```
 */
export function useMoments(): UseMomentsReturn {
  const results = useStore((s) => s.moments.results);
  const isGenerating = useStore((s) => s.moments.isGenerating);
  const error = useStore((s) => s.moments.error);
  
  const generateMomentAction = useStore((s) => s.generateMoment);
  const getMomentResultsAction = useStore((s) => s.getMomentResults);

  const generateMoment = useCallback(
    async (tryonCacheKey: string, momentType: string) => {
      return generateMomentAction(tryonCacheKey, momentType);
    },
    [generateMomentAction]
  );

  const getMomentResults = useCallback(
    (tryonCacheKey: string) => {
      return getMomentResultsAction(tryonCacheKey);
    },
    [getMomentResultsAction]
  );

  const hasMoment = useCallback(
    (tryonCacheKey: string, momentType: string) => {
      const momentResults = results[tryonCacheKey] || [];
      return momentResults.some((r) => r.momentType === momentType);
    },
    [results]
  );

  return {
    results,
    isGenerating,
    error,
    generateMoment,
    getMomentResults,
    hasMoment,
  };
}
